'use client'

import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { LargeMatterFailureIcon } from '@repo/ui/LargeMatterFailureIcon'
import { LargeMatterSuccessIcon } from '@repo/ui/LargeMatterSuccessIcon'
import { LargeNotesIcon } from '@repo/ui/LargeNotesIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useThirdPartyFormWizardStore } from '@repo/store/useThirdPartyFormWizardStore'
import { useWizardStore } from '@repo/store/useWizardStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'

// Mirrors MoreMenuCard.tsx's own FORMEVO_DISPLAY_URL/handleOpenInFormEvo pattern - not exported
// from that file, so redefined here rather than importing across an unrelated module boundary.
const FORMEVO_DISPLAY_URL = 'https://www.formevo.co.uk/myforms.html?action=form_display&_id='

// reviewData already only contains mapped rows (OS-2361's own Continue action filters to
// row.isMapped before writing it) - this only needs to further filter to row.send, and strip
// the send field the backend doesn't want.
const buildMappedFormFieldValues = (reviewData) =>
  (reviewData ?? []).filter((row) => row.send).map(({ mappedFormFieldId, value }) => ({ mappedFormFieldId, value }))

export const ThirdPartyFormConfirmation = ({ updateStepState, wideMode, handlePrevNext }) => {
  const { setWizardIsComplete } = useWizardStore()
  const { mappedFormId, reviewData, selectedMatter, createDocument } = useThirdPartyFormWizardStore()
  const [status, setStatus] = useState('loading') // 'loading' | 'error' | 'success'
  const [errorMessage, setErrorMessage] = useState('')
  const [providerDocumentId, setProviderDocumentId] = useState(null)

  const submit = useCallback(async () => {
    setStatus('loading')
    setErrorMessage('')

    try {
      const result = await createDocument({
        mappedFormId,
        clientNo: selectedMatter?.clientNo,
        matterNo: selectedMatter?.matterNo,
        mappedFormFieldValues: buildMappedFormFieldValues(reviewData)
      })

      if (!result?.isSuccess) {
        setErrorMessage(result?.errorMessage || '')
        setStatus('error')
        return
      }

      setProviderDocumentId(result.providerDocumentId)
      setStatus('success')
    } catch (err) {
      setErrorMessage(err?.message || '')
      setStatus('error')
    }
  }, [createDocument, mappedFormId, reviewData, selectedMatter])

  useEffect(() => {
    submit()
    // Submit once, on mount, regardless of later store/callback identity changes - re-submission
    // only ever happens via the explicit "Try again" action below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClickContinue = () => {
    updateStepState({ status: 'completed' })
    setWizardIsComplete(true)
  }

  const handleOpenInFormEvo = () => {
    if (providerDocumentId) {
      window.open(`${FORMEVO_DISPLAY_URL}${providerDocumentId}`, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <>
      <WizardPanel data-testid="ThirdPartyFormConfirmation">
        {status === 'loading' && (
          <>
            <WizardIconTitle
              title="Confirmation"
              text="Creating your form - this can take a few seconds."
              icon={LargeNotesIcon}
              sx={{ width: '100%' }}
            />
            <LoadingState sx={{ width: '64px', height: '64px', mt: 4 }} />
          </>
        )}

        {status === 'error' && (
          <>
            <WizardIconTitle title="Something went wrong" icon={LargeMatterFailureIcon} sx={{ width: '100%' }} />
            <MUIAlert severity="error" sx={{ mt: 2, width: '100%' }}>
              {errorMessage || 'Unable to create the form. Please try again.'}
            </MUIAlert>
            <MUIButton variant="contained" sx={{ mt: 4 }} onClick={submit}>
              Try again
            </MUIButton>
          </>
        )}

        {status === 'success' && (
          <>
            <WizardIconTitle
              title="Form created"
              text="Your form has been created successfully."
              icon={LargeMatterSuccessIcon}
              sx={{ width: '100%' }}
            />
            <MUIButton
              variant="contained"
              sx={{ mt: 4 }}
              onClick={handleOpenInFormEvo}
              data-testid="OpenInFormEvoButton"
            >
              Open in FormEvo
            </MUIButton>
          </>
        )}
      </WizardPanel>
      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleClickContinue}
        handleClickBack={() => handlePrevNext(-1)}
        continueButtonText="Create form"
        continueButtonDisabled={status !== 'success'}
      />
    </>
  )
}

ThirdPartyFormConfirmation.propTypes = {
  updateStepState: PropTypes.func,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func
}
